import { useEffect, useRef, useState } from 'react';
import { bucketLabel, useA } from '@/analytics/lib';
import { money } from './ui';

// The prototype's revenue-over-time chart: a daily line with its gradient, the previous period dashed, $k gridlines,
// seven date labels and a hover tooltip — drawn at the container's real width so the text never stretches.
export function useDailySeries(state, metric) {
  const ts = useA('/timeseries', state, { granularity: 'day' });
  const prev = useA('/timeseries', state, { granularity: 'day' }, { range: state.prev, enabled: !!state.compare });
  const pick = d => (d?.series || []).map(b => ({ bucket: b.bucket, value: b[metric] || 0 }));
  return { query: ts, series: pick(ts.data), prevSeries: pick(prev.data) };
}

export function RevenueChart({ series, prevSeries, compare, metric = 'revenue', label = 'Revenue' }) {
  const ref = useRef(null);
  const [width, setWidth] = useState(780);
  const [tip, setTip] = useState(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(entries => { const w = entries[0]?.contentRect?.width; if (w) setWidth(Math.max(260, w - 32)); });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const w = width, h = 196, left = 43, right = 13, top = 16, bottom = 29, innerH = h - top - bottom, innerW = w - left - right;
  const vals = series.map(d => d.value);
  const count = vals.length;
  const fmt = v => (metric === 'revenue' ? money(v) : String(Math.round(v)));
  if (count < 2) return <div ref={ref} className="chart-container"><div className="empty-state" style={{ padding: '40px 20px' }}><p>Not enough days in this period to draw a trend.</p></div></div>;
  const max = Math.max(1, ...vals, ...(compare ? prevSeries.map(d => d.value) : [])) * 1.18;
  const nice = metric === 'revenue' ? Math.max(1000, Math.ceil(max / 1000) * 1000) : Math.max(10, Math.ceil(max / 10) * 10);
  const x = i => left + (i / (count - 1)) * innerW;
  const y = v => top + innerH * (1 - v / nice);
  const path = vals.map((v, i) => `${i ? 'L' : 'M'}${x(i).toFixed(2)},${y(v).toFixed(2)}`).join(' ');
  const prevVals = prevSeries.map(d => d.value);
  const prevPath = prevVals.length > 1 ? prevVals.map((v, i) => `${i ? 'L' : 'M'}${(left + (i / (prevVals.length - 1)) * innerW).toFixed(2)},${y(v).toFixed(2)}`).join(' ') : '';
  const labelIdx = Array.from({ length: Math.min(7, count) }, (_, i) => Math.round((i / (Math.min(7, count) - 1)) * (count - 1)));
  const gridLabel = v => (metric === 'revenue' ? (v >= 1000 ? `$${(v / 1000).toFixed(v % 1000 ? 1 : 0)}k` : `$${Math.round(v)}`) : String(Math.round(v)));
  return (
    <div ref={ref} className="chart-container">
      <svg className="main-chart" viewBox={`0 0 ${w} ${h}`} width={w} height={h} role="img" aria-label={`${label} by day`}>
        <defs><linearGradient id="revenue-fill" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#3867ef" stopOpacity=".10" /><stop offset="100%" stopColor="#3867ef" stopOpacity="0" /></linearGradient></defs>
        {[0, 1, 2, 3].map(i => { const gy = top + (i / 3) * innerH; return <g key={i}><line x1={left} x2={w - right} y1={gy} y2={gy} stroke="#e9eae5" strokeDasharray="3 4" /><text x="0" y={gy + 3}>{gridLabel(nice * (1 - i / 3))}</text></g>; })}
        <path d={`${path}L${w - right},${top + innerH}L${left},${top + innerH}Z`} fill="url(#revenue-fill)" />
        {compare && prevPath && <path d={prevPath} stroke="#b7bfcc" strokeWidth="1.4" strokeDasharray="4 4" fill="none" />}
        <path d={path} stroke="#3867ef" strokeWidth="2" fill="none" strokeLinejoin="round" />
        <circle cx={x(count - 1)} cy={y(vals[count - 1])} r="3" fill="#3867ef" stroke="white" strokeWidth="1.5" />
        {labelIdx.map((idx, i) => <text key={idx} x={x(idx)} y={h - 5} textAnchor={i === 0 ? 'start' : i === labelIdx.length - 1 ? 'end' : 'middle'}>{bucketLabel(series[idx].bucket, 'day')}</text>)}
        {vals.map((v, i) => <rect key={i} x={x(i) - innerW / (2 * (count - 1))} y={top} width={innerW / (count - 1)} height={innerH} fill="transparent"
          onMouseEnter={() => setTip({ i, v })} onMouseLeave={() => setTip(null)}><title>{fmt(v)}</title></rect>)}
      </svg>
      <div className={`chart-tooltip${tip ? ' visible' : ''}`} style={tip ? { left: Math.min(width - 175, Math.max(45, (tip.i / (count - 1)) * (width - 50))) } : undefined}>
        {tip && <><span className="muted">{bucketLabel(series[tip.i].bucket, 'day', true)}</span><br /><span className="blue">{label}</span> <strong>{fmt(tip.v)}</strong></>}
      </div>
    </div>
  );
}
